import { useCallback, useState, useMemo } from 'react';
import { useMotionValue, useTransform, PanInfo } from 'framer-motion';
import { X, ExternalLink, Shirt, Share2, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { SaveButton } from '@/components/ui/SaveButton';
import { Button } from '@/components/ui/button';
import { openExternalUrl } from '@/utils/openExternalUrl';
import { usePortalContainer } from '@/contexts/PhoneFrameContext';
import { ShareSheet } from '@/components/share/ShareSheet';
import { useSavedItems } from '@/hooks/useSavedItems';
import { toast } from 'sonner';

export interface PreviewProduct {
  name: string;
  image: string;
  url: string;
  price?: number | null;
  currency?: string;
  store?: string;
  /** Saved item ID — if provided, enables "Send to Friends" in share */
  id?: string;
}

interface ProductPreviewLightboxProps {
  open: boolean;
  onClose: () => void;
  product: PreviewProduct | null;
  onSave: () => void;
  isSaved: boolean;
  onTryOn: () => void;
  /** @deprecated Use built-in ShareSheet instead */
  onShare?: () => void;
  onViewDetails?: () => void;
  formatPrice?: (price: number | null, currency?: string) => string | null;
  /** Optional saved item ID — enables "Send to Friends" in the share sheet */
  savedItemId?: string;
}

export function ProductPreviewLightbox({
  open,
  onClose,
  product,
  onSave,
  isSaved: isSavedProp,
  onTryOn,
  onShare: _onShareLegacy,
  onViewDetails,
  formatPrice,
  savedItemId: savedItemIdProp,
}: ProductPreviewLightboxProps) {
  const portalContainer = usePortalContainer();
  const [showShareSheet, setShowShareSheet] = useState(false);
  const { items, deleteItem } = useSavedItems();
  const dragY = useMotionValue(0);
  const backdropOpacity = useTransform(dragY, [0, 300], [1, 0.2]);

  const handleDragEnd = useCallback((_: never, info: PanInfo) => {
    if (info.offset.y > 100 || info.velocity.y > 500) {
      onClose();
    }
  }, [onClose]);

  // Check if the product is already in saved items by matching URL or image
  const matchedSavedItem = useMemo(() => {
    if (!product || !open) return null;
    const url = product.url?.trim();
    const image = product.image?.trim();
    return items.find(item => {
      if (url && item.url && item.url.trim() === url) return true;
      if (image && item.image_url && item.image_url.trim() === image) return true;
      return false;
    }) ?? null;
  }, [product, items, open]);

  // Track items saved within this lightbox session (for optimistic UI before query refetch)
  const [justSavedUrl, setJustSavedUrl] = useState<string | null>(null);

  const isSaved = !!matchedSavedItem || isSavedProp || (justSavedUrl === (product?.url || product?.image));

  const handleToggleSave = useCallback(() => {
    if (!product) return;
    
    if (isSaved && matchedSavedItem) {
      // Unsave: delete from saved items
      deleteItem.mutate(matchedSavedItem.id, {
        onSuccess: () => {
          setJustSavedUrl(null);
        },
        onError: () => {
          toast.error('Failed to remove item');
        },
      });
    } else if (!isSaved) {
      // Save: call the parent onSave
      onSave();
      setJustSavedUrl(product.url || product.image);
    }
  }, [product, isSaved, matchedSavedItem, deleteItem, onSave]);

  // Reset optimistic state when product changes
  const [lastProductKey, setLastProductKey] = useState<string | null>(null);
  const currentKey = product ? (product.url || product.image) : null;
  if (currentKey !== lastProductKey) {
    setLastProductKey(currentKey);
    setJustSavedUrl(null);
  }

  const handleVisitRetailer = useCallback(() => {
    if (product?.url) {
      openExternalUrl(product.url);
    }
  }, [product?.url]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const formattedPrice = product?.price && formatPrice
    ? formatPrice(product.price, product.currency)
    : null;

  const content = (
    <AnimatePresence>
      {open && product && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[70] bg-black/90 flex flex-col"
          onClick={handleBackdropClick}
          style={{ opacity: backdropOpacity, y: dragY }}
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 0.6 }}
          onDragEnd={handleDragEnd}
        >
          {/* Top zone — full-width tap target to close */}
          <div
            className="flex justify-end p-4 pb-2 pt-[calc(1rem+env(safe-area-inset-top))] shrink-0 cursor-pointer"
            onClick={onClose}
          >
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center text-white active:bg-white/25 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Image area - pinch to zoom */}
          <div className="flex-1 flex items-center justify-center px-4 py-2 min-h-0 overflow-hidden" onClick={handleBackdropClick}>
            <div
              className="relative w-full max-w-md"
              style={{ touchAction: 'pinch-zoom' }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-auto max-h-[55vh] object-contain rounded-xl pointer-events-none select-none"
                draggable={false}
                style={{ WebkitTouchCallout: 'none' }}
              />

              {/* Action buttons overlaid on image - top right */}
              <div className="absolute top-3 right-3 flex flex-col gap-2.5">
                <SaveButton saved={isSaved} onToggle={handleToggleSave} size="md" variant="overlay" />
                <button
                  onClick={onTryOn}
                  className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-md text-white border border-white/20 shadow-lg flex items-center justify-center active:bg-black/60 transition-all"
                >
                  <Shirt className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowShareSheet(true)}
                  className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-md text-white border border-white/20 shadow-lg flex items-center justify-center active:bg-black/60 transition-all"
                >
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Bottom bar - product info + CTA */}
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 bg-gradient-to-t from-black/80 to-transparent"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-w-md mx-auto space-y-3">
              {/* Product info */}
              <div>
                {product.store && (
                  <p className="text-xs text-white/60">{product.store}</p>
                )}
                <p className="text-sm font-medium text-white line-clamp-2">{product.name}</p>
                {formattedPrice && (
                  <p className="text-base font-semibold text-white mt-0.5">{formattedPrice}</p>
                )}
              </div>

              {/* CTAs */}
              <div className="flex gap-2">
                {product.url && (
                  <Button
                    onClick={handleVisitRetailer}
                    className="flex-1 gap-2"
                    size="lg"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Buy
                  </Button>
                )}
                {onViewDetails && (
                  <Button
                    onClick={onViewDetails}
                    variant="outline"
                    className="flex-1 gap-2 bg-white text-black hover:bg-white/90 hover:text-black border-0"
                    size="lg"
                  >
                    <Info className="w-4 h-4" />
                    View details
                  </Button>
                )}
              </div>
            </div>
          </motion.div>

          {/* ShareSheet - internal + external sharing */}
          <ShareSheet
            title={product.name}
            url={product.url}
            imageUrl={product.image}
            storeName={product.store}
            savedItemId={matchedSavedItem?.id || savedItemIdProp || product.id}
            product={{
              name: product.name,
              image_url: product.image,
              url: product.url,
              price: product.price,
              currency: product.currency,
              store: product.store,
            }}
            open={showShareSheet}
            onOpenChange={setShowShareSheet}
            zIndexClass="z-[80]"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(content, portalContainer);
}

// Hook for using draggable sheet with AnimatePresence
export function useDraggableSheet(initialOpen = false) {
  const [isOpen, setIsOpen] = useState(initialOpen);
  
  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen(prev => !prev),
    onOpenChange: setIsOpen,
  };
}
